import { motion } from 'framer-motion';
import { ArrowLeft, Edit, MapPin, Target, Trash2, Users } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import leaveApi from '../LeaveManagement/leaveApi';
import userService from '../../services/userService';
import PrimaryButton from '../ui/PrimaryButton';
import {
  deleteSite,
  getSiteDetails,
  parseGeofenceDetails,
  resolveSiteLogoUrl,
  siteHasLogo,
} from './siteApi';
import './SiteManagement.css';

const SiteDetails = ({
  siteId,
  refreshKey = 0,
  onBack,
  onEdit,
  onDelete,
  onCommunity,
  isSidebar = false,
}) => {
  const [site, setSite] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const logoObjectUrlRef = useRef(null);
  const logoRetryRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);

  // Permission required to convert a site into community onboarding flow
  const convertToCommunityPermission = 'onboarding.site.create.community';
  const canConvertToCommunity = permissions.includes(convertToCommunityPermission);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const userPermissions = await userService.getUserPermissions();
        setPermissions(Array.isArray(userPermissions) ? userPermissions : []);
      } catch (e) {
        setPermissions([]);
      } finally {
        setIsLoadingPermissions(false);
      }
    };
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (siteId) {
      fetchSiteDetails();
    }
  }, [siteId, refreshKey]);

  const clearLogoObjectUrl = () => {
    if (logoObjectUrlRef.current) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
      logoObjectUrlRef.current = null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadLogo = async () => {
      clearLogoObjectUrl();
      logoRetryRef.current = false;

      if (!site) {
        setLogoUrl(null);
        setLogoLoading(false);
        return;
      }

      setLogoLoading(true);
      const resolvedUrl = await resolveSiteLogoUrl(site);
      if (cancelled) {
        if (resolvedUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(resolvedUrl);
        }
        return;
      }

      if (resolvedUrl?.startsWith('blob:')) {
        logoObjectUrlRef.current = resolvedUrl;
      }

      setLogoUrl(resolvedUrl);
      setLogoLoading(false);
    };

    loadLogo();

    return () => {
      cancelled = true;
      clearLogoObjectUrl();
    };
  }, [site]);

  const handleLogoError = async () => {
    if (!site?.site_id || logoRetryRef.current) {
      setLogoUrl(null);
      return;
    }

    logoRetryRef.current = true;
    clearLogoObjectUrl();
    setLogoLoading(true);

    const refreshedUrl = await resolveSiteLogoUrl({
      ...site,
      logo_presigned_url: null,
      logo_url: null,
      logoUrl: null,
    });

    setLogoLoading(false);

    if (!refreshedUrl) {
      setLogoUrl(null);
      return;
    }

    if (refreshedUrl.startsWith('blob:')) {
      logoObjectUrlRef.current = refreshedUrl;
    }

    setLogoUrl(refreshedUrl);
  };

  // Fetch supervisor details when site data is loaded
  const fetchSupervisorDetails = useCallback(async () => {
    if (!site || !site.authorised_ids || site.authorised_ids.length === 0) {
      setSupervisors([]);
      return;
    }

    setLoadingSupervisors(true);
    try {
      const supervisorDetailsPromises = site.authorised_ids.map(async supervisorId => {
        try {
          const supervisorInfo = await leaveApi.getUserInfo(supervisorId);
          return {
            id: supervisorId,
            name: supervisorInfo?.user_name || supervisorInfo?.name || supervisorId,
            employee_id: supervisorInfo?.employee_id || supervisorId,
            ...supervisorInfo,
          };
        } catch (error) {
          console.error(`Error fetching supervisor details for ${supervisorId}:`, error);
          // Return fallback info if API call fails
          return {
            id: supervisorId,
            name: supervisorId,
            employee_id: supervisorId,
          };
        }
      });

      const supervisorDetails = await Promise.all(supervisorDetailsPromises);
      setSupervisors(supervisorDetails);
    } catch (error) {
      console.error('Error fetching supervisor details:', error);
      setSupervisors([]);
    } finally {
      setLoadingSupervisors(false);
    }
  }, [site]);

  useEffect(() => {
    if (site && site.authorised_ids && site.authorised_ids.length > 0) {
      fetchSupervisorDetails();
    } else {
      setSupervisors([]);
    }
  }, [site, fetchSupervisorDetails]);

  const fetchSiteDetails = async () => {
    try {
      setLoading(true);
      const siteData = await getSiteDetails(siteId);
      setSite(siteData);
      setError(null);
    } catch (err) {
      console.error('Error fetching site details:', err);
      setError('Failed to load site details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSite(siteId);
      onDelete(siteId);
    } catch (err) {
      console.error('Error deleting site:', err);
      alert('Failed to delete site. Please try again.');
    }
  };

  const getGeofenceDisplay = geofenceDetails => {
    try {
      const details = parseGeofenceDetails(geofenceDetails);

      if (details.type === 'Polygon') {
        return `${details.coordinates?.length || 0} points`;
      } else if (details.type === 'Circle') {
        return `${details.radius || 0}m radius`;
      }
      return 'Unknown type';
    } catch (error) {
      return 'Invalid data';
    }
  };

  const getGeofenceIcon = geofenceDetails => {
    try {
      const details = parseGeofenceDetails(geofenceDetails);
      return details.type === 'Polygon' ? (
        <Target className="w-5 h-5" />
      ) : (
        <MapPin className="w-5 h-5" />
      );
    } catch (error) {
      return <MapPin className="w-5 h-5" />;
    }
  };

  const formatTimestamp = timestamp => {
    if (!timestamp) return 'Not available';
    try {
      const date = new Date(parseFloat(timestamp) * 1000);
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid timestamp';
    }
  };

  if (loading) {
    return (
      <div className="site-details-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading site details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="site-details-container">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchSiteDetails} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="site-details-container">
        <div className="error-container">
          <p className="error-message">Site not found</p>
          <button onClick={onBack} className="retry-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`site-details-container ${isSidebar ? 'sidebar-mode' : ''}`}
    >
      {/* Header */}
      <div className="site-details-header">
        <button onClick={onBack} className="back-button">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="site-details-title">Site Details</h2>
        <div className="header-actions">
          <button onClick={() => onEdit(site)} className="edit-site-button" title="Edit Site">
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeleteConfirm(true)}
            className="delete-site-button"
            title="Delete Site"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Site Information */}
      <div className="site-details-content">
        <motion.div
          className="site-info-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="section-title">Basic Information</h3>

          <div className="site-details-logo-wrap">
            <label className="info-label">Site Logo</label>
            {logoUrl ? (
              <div className="site-details-logo">
                <img
                  src={logoUrl}
                  alt={`${site.site_name || 'Site'} logo`}
                  className="site-details-logo-image"
                  onError={handleLogoError}
                />
              </div>
            ) : (
              <div className="site-details-logo site-details-logo-placeholder">
                {logoLoading
                  ? 'Loading logo...'
                  : siteHasLogo(site)
                    ? 'Logo unavailable'
                    : 'No logo uploaded'}
              </div>
            )}
          </div>

          <div className="info-grid">
            <div className="info-item">
              <label className="info-label">Site Name</label>
              <span className="info-value">
                {site.site_name || 'Not specified'}
              </span>
            </div>

            {/* <div className="info-item">
              <label className="info-label">Site ID</label>
              <span className="info-value">
                {site.site_id || 'Not available'}
              </span>
            </div> */}

            <div className="info-item full-width">
              <label className="info-label">Address</label>
              <span className="info-value address-value">
                {site.address || 'No address provided'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Geofence Information */}
        <motion.div
          className="site-info-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <h3 className="section-title">Geofence Configuration</h3>

          <div className="geofence-info">
            <div className="geofence-type">
              {getGeofenceIcon(site.geofence_details)}
              <span className="geofence-type-text">
                {parseGeofenceDetails(site.geofence_details)?.type || 'Unknown'}{' '}
                Type
              </span>
            </div>

            <div className="geofence-details">
              <span className="geofence-detail-text">
                {getGeofenceDisplay(site.geofence_details)}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Supervisor Information */}
        <motion.div
          className="site-info-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <h3 className="section-title">Employees</h3>

          <div className="supervisors-info">
            {loadingSupervisors ? (
              <p className="no-supervisors">Loading supervisor details...</p>
            ) : supervisors.length > 0 ? (
              <div className="supervisors-list">
                {supervisors.map((supervisor, index) => (
                  <div key={supervisor.id || index} className="supervisor-item">
                    <Users className="w-4 h-4" />
                    <span className="supervisor-id">
                      {supervisor.name} {supervisor.employee_id && `(${supervisor.employee_id})`}
                    </span>
                  </div>
                ))}
              </div>
            ) : site.authorised_ids && site.authorised_ids.length > 0 ? (
              <div className="supervisors-list">
                {site.authorised_ids.map((supervisorId, index) => (
                  <div key={index} className="supervisor-item">
                    <Users className="w-4 h-4" />
                    <span className="supervisor-id">{supervisorId}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-supervisors">
                No supervisors assigned to this site
              </p>
            )}
          </div>
        </motion.div>

        {/* System Information */}
        <motion.div
          className="site-info-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <h3 className="section-title">System Information</h3>

          <div className="info-grid">
            <div className="info-item">
              <label className="info-label">Bluetooth Enabled</label>
              <span
                className={`info-value ${
                  site.bluetooth_enabled ? 'enabled' : 'disabled'
                }`}
              >
                {site.bluetooth_enabled ? 'Yes' : 'No'}
              </span>
            </div>

            {/* <div className="info-item">
              <label className="info-label">Created By</label>
              <span className="info-value">{site.activity_by || 'System'}</span>
            </div> */}

            <div className="info-item">
              <label className="info-label">Created On</label>
              <span className="info-value">
                {formatTimestamp(site.activity_timestamp)}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="site-details-actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          {!isLoadingPermissions && canConvertToCommunity ? (
            <PrimaryButton
              className="w-full h-12 text-base font-semibold"
              onClick={() => onCommunity?.(site)}
            >
              Make the site as community
            </PrimaryButton>
          ) : null}
        </motion.div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirmation-modal">
            <h3>Delete Site</h3>
            <p>
              Are you sure you want to delete "{site.site_name}"? This action
              cannot be undone and may affect assigned employees and shifts.
            </p>
            <div className="modal-actions">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="cancel-button"
              >
                Cancel
              </button>
              <button onClick={handleDelete} className="delete-confirm-button">
                Yes, Delete Site
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteDetails;
